import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Button,
  ButtonStyles,
  ButtonWidth,
  CardRadioButton,
  Drawer,
  Input,
  FAIcon,
  InputTypes,
  MLPVersion,
  Theme,
  useTranslation,
  InputValidations,
  useFeatureVisibility,
} from '@nextgen-web-framework/all';
import {
  ExceptionsEnum,
  getAllExceptionWithSeverity,
  Severity,
  type InitialCartState,
  cartApi,
  ApiProjections,
  CartConstantsEnum,
  KeyboardKeys,
  isProxyOrder,
  AmwayUserDetailsStatusEnum,
  SubCartDetail,
  SWITCH_PROXY_TYPE,
} from '@commerce-cart-webcomponents/sdk';
import { type AppState, StorageService } from '@nextgen-web-framework/core';
import { useSelector } from 'react-redux';
import { AmwayUserDetails } from '@commerce-cart-webcomponents/sdk/services/models/AmwayUserDetails';
import { useCartNotification } from '../../../../common/hooks/useCartNotification';
import { CartNotificationTheme } from '../../../../common/components/cart-notification/types/enums';
import { TOAST_TIMEOUT } from '../../../types/enums';
import { DownlineSearchLoader } from '../../cart-loader/downline-search-loader/DownlineSearchLoader';
import styles from './DrawerForm.module.scss';

export type DrawerFormProps = {
  onDrawerClose?: () => void;
  onCancelClick?: () => void;
  onConfirmClick?: () => void;
  headerTitle?: string;
  inputPlaceHolderText?: string;
  inputValue: string;
  onTextChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  data: AmwayUserDetails[];
  checkedIndex?: number;
  onRadioChange: (account: AmwayUserDetails, index: number) => void;
  resultTitle?: string;
  subHeading?: string;
  cancelButtonTitle?: string;
  confirmButtonTitle?: string;
  disableConfirmButton?: boolean;
  showLoader?: boolean;
  interSectionObserverCallBack?: (text: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSearchClick?: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  hidePrimaryButton?: boolean;
  showActiveCarts?: boolean;
  showDisableCards?: boolean;
  showNoResultText?: boolean;
  modalConfirmClick?: (switchCartType?: SWITCH_PROXY_TYPE) => void;
  showSwitchModal?: boolean;
  isSopEntryExist?: boolean;
  isInstallmentExist?: boolean;
};

export function DrawerForm({
  onDrawerClose,
  onCancelClick,
  onConfirmClick,
  headerTitle,
  inputPlaceHolderText,
  onTextChange,
  data = [],
  checkedIndex,
  onRadioChange,
  resultTitle,
  cancelButtonTitle,
  confirmButtonTitle,
  disableConfirmButton,
  interSectionObserverCallBack,
  onKeyDown,
  onSearchClick,
  showLoader,
  subHeading,
  hidePrimaryButton = false,
  showActiveCarts = false,
  showDisableCards = true,
  showNoResultText = false,
  modalConfirmClick,
  showSwitchModal = false,
  isSopEntryExist,
  isInstallmentExist,
}: DrawerFormProps) {
  const t = useTranslation();
  const observer = useRef<IntersectionObserver>();
  const [inputText, setInputText] = useState<string>('');
  const [showResults, setShowResults] = useState<boolean>(true);
  const [searchIconClick, setSearchIconClick] = useState<boolean>(false);
  const [subCartsByUser, setSubCartsByUser] = useState<SubCartDetail[]>([]);
  const { createToast, deleteToast } = useCartNotification('DrawerForm');
  const accoutExceptionText = t('cart.addToCartDrawer.accountException');
  const enterAtleast3Char = t('cart.drawerForm.enterAtleast3Char');
  const [inputError, setInputError] = useState<boolean>(false);
  const cartOverrideFlag = useFeatureVisibility?.('cart.cartoverride') ?? true;

  const [switchCartRadioButtonSelection, setSwitchCartRadioButtonSelection] = useState<SWITCH_PROXY_TYPE>();
  const [showModal, setShowModal] = useState(false);

  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { cart }: InitialCartState = commerceCartState;
  const noResultFoundText = t('cart.DrawerForm.noResultFound');
  const proxyModalHeadLine = t('cart.DrawerForm.proxyModalHeadLine');
  const subTextOverride = t('cart.DrawerForm.subTextOverride');
  const subTextOverrideSOP = t('cart.DrawerForm.subTextOverrideWithSOP');
  const subTextOverrideInstalments = t('cart.DrawerForm.subTextOverrideWithInstallment');
  const subTextSwitch = t('cart.DrawerForm.subTextSwitch');
  const confirmButtonModalTitle = t('cart.DrawerForm.confirmButtonModalTitle');
  const cancelButtonModalTitle = t('cart.DrawerForm.cancelButtonModalTitle');
  const overrideDescription = t('cart.DrawerForm.overrideDescription');
  const switchDescription = t('cart.DrawerForm.switchDescription');

  const getOverrideText = () => {
    if (isSopEntryExist) {
      return subTextOverrideSOP;
    }

    if (isInstallmentExist) {
      return subTextOverrideInstalments;
    }

    return subTextOverride;
  };

  useEffect(() => {
    const localCartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
    showActiveCarts &&
      localCartId &&
      cartApi
        .getSubCart({
          cartId: localCartId,
          subCartId: localCartId,
          projections: ApiProjections.FULL,
          options: {
            skipCartResStoreInLS: true,
            skipCartIdStoreInLS: true,
            skipVersionStoreInLS: true,
          },
        })
        .then((res) => {
          const { subCarts } = res;
          const filteredSubCart = subCarts.filter(({ subCartId }) => subCartId !== localCartId);
          setSubCartsByUser(filteredSubCart);
        });
  }, []);

  useEffect(() => {
    if (inputText !== '') {
      setShowResults(true);
    } else {
      setShowResults(false);
      setSearchIconClick(false);
    }
    if (inputText.length <= 3) {
      setSearchIconClick(false);
    }
  }, [inputText]);

  const lastElement = useCallback((node: Element) => {
    if (interSectionObserverCallBack) {
      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            interSectionObserverCallBack(inputText);
          }
        },
        {
          rootMargin: '5px',
        },
      );

      if (node) {
        observer.current.observe(node);
      }
    }
  }, []);

  const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiltered = e.target.value;
    const trimmedText = inputFiltered.trimEnd().trimStart();
    setInputError(false);
    const accountException = getAllExceptionWithSeverity(
      [ExceptionsEnum.Exceptions.AccountSearchService],
      Severity.Warn,
      cart.errors ?? [],
    );

    if (accountException?.length) {
      createToast(
        {
          theme: CartNotificationTheme.RED,
          rightSeperator: false,
          content: accoutExceptionText,
          clickIcon: 'times',
          icon: 'exclamation-square',
          timeOut: TOAST_TIMEOUT,
        },
        deleteToast,
      );

      return;
    }

    setInputError(trimmedText.length < 3 && trimmedText.length > 0);
    if (onTextChange) {
      onTextChange(e);
    }
    setInputText(inputFiltered);
  };

  const drawerOnCrossHandler = () => {
    setInputText('');
    onDrawerClose && onDrawerClose();
  };

  const drawerOnCancelHandler = () => {
    setInputText('');
    onCancelClick && onCancelClick();
  };

  const handleSubCartSearchIcon = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    if (!validateInput()) {
      return;
    }
    onSearchClick && onSearchClick(e);
    setSearchIconClick(true);
  };

  const handleSubCartSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown && onKeyDown(e);
    if (e.key === KeyboardKeys.ENTER) {
      if (!validateInput()) {
        return;
      }
      setSearchIconClick(true);
    }
  };

  // Validate Input
  const validateInput = () => {
    setInputError(inputText.length < 3);
    return inputText.length >= 3;
  };

  const onCardRadioClick = () => {
    if (showSwitchOrOverrideModal()) {
      !cartOverrideFlag && setShowModal(true);
      cartOverrideFlag && modalConfirmClick?.(SWITCH_PROXY_TYPE.OVERRIDE);
      return;
    }
    onConfirmClick?.();
  };

  const renderAccounts = () => {
    if (data.length > 0 && showResults && (searchIconClick || inputText.length >= 3)) {
      return (
        <div className={styles['account-name-container-results']} data-testid="drawer-test-result">
          <div className={styles['account-name-container-results-title']}>{resultTitle}</div>
          <div className={styles['account-name-container-results-wrapper']}>
            {data.map(({ amwayUserId, accountId, firstName, lastName, status }, i) => {
              const checkedClass = checkedIndex === i ? styles['result-checked'] : '';
              const isAccountIdExist = cart.subCarts?.find((subcart) => subcart.amwayUser?.accountId === accountId);
              const disableCard = showDisableCards ? !!isAccountIdExist : false;
              if (i === data.length - 1) {
                return (
                  <div ref={lastElement as React.LegacyRef<HTMLDivElement>} className={checkedClass} key={accountId}>
                    <CardRadioButton
                      disabled={disableCard || status === AmwayUserDetailsStatusEnum.Inactive}
                      checked={checkedIndex === i}
                      mlpVersion={MLPVersion.ONE}
                      label={`${firstName} ${lastName}`}
                      name="account-number-or-name"
                      cardDescription={`${t('cart.cartEllipsesMenu.accountNum')} ${accountId}`}
                      onChange={() =>
                        onRadioChange(
                          {
                            amwayUserId,
                            accountId,
                            firstName,
                            lastName,
                          },
                          i,
                        )
                      }
                      data-testid="drawer-search-result-item"
                    />
                  </div>
                );
              }
              return (
                <div className={checkedClass} key={accountId}>
                  <CardRadioButton
                    onClick={onCardRadioClick}
                    disabled={disableCard || status === AmwayUserDetailsStatusEnum.Inactive}
                    checked={checkedIndex === i}
                    mlpVersion={MLPVersion.ONE}
                    label={`${firstName} ${lastName}`}
                    name="account-number-or-name"
                    cardDescription={`${t('cart.cartEllipsesMenu.accountNum')} ${accountId}`}
                    onChange={() =>
                      onRadioChange(
                        {
                          amwayUserId,
                          accountId,
                          firstName,
                          lastName,
                        },
                        i,
                      )
                    }
                    data-testid="drawer-search-result-item"
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (showNoResultText) {
      return <div className={styles['no-result-found-text']}>{noResultFoundText.replace('{0}', inputText)}</div>;
    }

    return <></>;
  };

  const showActivesCart = () =>
    showActiveCarts && !!subCartsByUser.length && !data.length && !showLoader && inputText.length === 0;

  const showSwitchOrOverrideModal = () => {
    if (typeof checkedIndex === 'number') {
      if (data.length) {
        //when switching to subcarts from search
        return !!subCartsByUser.find(
          ({ amwayUser, entries }) =>
            (amwayUser?.amwayUserId === data[checkedIndex]?.amwayUserId && entries?.length) ||
            (!data[checkedIndex] && amwayUser?.amwayUserId && entries?.length),
        );
      } else {
        //when switching to subcarts from recent carts
        const updatedCart = subCartsByUser[checkedIndex];
        return !!updatedCart?.entries?.length;
      }
    }

    return false;
  };

  const showOverrideDrawer = showSwitchModal && showSwitchOrOverrideModal() && showModal && !cartOverrideFlag;

  return (
    <>
      {showOverrideDrawer && (
        <Drawer
          header={proxyModalHeadLine}
          footer={
            <div data-testid="card-radio-button-buttons-id" className={styles['card-radio-button-buttons']}>
              <Button
                buttonWidth={ButtonWidth.FULL_WIDTH}
                onClick={() => setShowModal(false)}
                buttonStyle={ButtonStyles.OUTLINE}
                title={cancelButtonModalTitle}
              />
              <Button
                buttonWidth={ButtonWidth.FULL_WIDTH}
                disabled={!switchCartRadioButtonSelection}
                onClick={() => modalConfirmClick?.(switchCartRadioButtonSelection)}
                title={confirmButtonModalTitle}
              />
            </div>
          }
        >
          <div className={styles['card-radio-button']}>
            <CardRadioButton
              onChange={() => setSwitchCartRadioButtonSelection(SWITCH_PROXY_TYPE.OVERRIDE)}
              checked={switchCartRadioButtonSelection === SWITCH_PROXY_TYPE.OVERRIDE}
              divider={true}
              data-testid="card-radio-override-id"
              cardDescription={overrideDescription}
            >
              <span className={styles['card-radio-button-subtext']}>{getOverrideText()}</span>
            </CardRadioButton>

            <CardRadioButton
              onChange={() => setSwitchCartRadioButtonSelection(SWITCH_PROXY_TYPE.SWITCH)}
              checked={switchCartRadioButtonSelection === SWITCH_PROXY_TYPE.SWITCH}
              divider={true}
              data-testid="card-radio-switch-id"
              cardDescription={switchDescription}
            >
              <span className={styles['card-radio-button-subtext']}>{subTextSwitch}</span>
            </CardRadioButton>
          </div>
        </Drawer>
      )}
      {!showOverrideDrawer && (
        <Drawer
          closeIcon={true}
          bottomGap={0}
          onClose={drawerOnCrossHandler}
          header={
            <div className={styles['drawer-header-title']} data-testid="drawer-header-txt">
              {headerTitle}
            </div>
          }
          useReactPortal={!showModal}
          portalContainerId={'react-portal-drawer'}
        >
          <div className={styles['account-name-container']}>
            <div>
              {!!subHeading && <span className={styles['account-name-search-sub-heading']}>{subHeading}</span>}
              <div className={styles['account-name-search']}>
                <Input
                  type={InputTypes.TEXT}
                  label={inputPlaceHolderText}
                  mlpVersion={MLPVersion.TWO}
                  value={inputText}
                  onChange={handleInputChanged}
                  onKeyDown={(e) => {
                    handleSubCartSearchKey(e);
                  }}
                  name="account-name-or-number"
                  className={styles['account-name-search-input']}
                  data-testid="drawer-search-input"
                  status={
                    inputError && inputText.trimEnd().trimStart().length < 3
                      ? InputValidations.INVALID
                      : InputValidations.DEFAULT
                  }
                  message={
                    inputError &&
                    inputText.trimEnd().trimStart().length < 3 &&
                    inputText.trimEnd().trimStart().length > 0
                      ? enterAtleast3Char
                      : ''
                  }
                />
                <FAIcon
                  iconName="search"
                  theme={Theme.BLACK}
                  className={styles['account-name-search-icon']}
                  onClick={(e) => handleSubCartSearchIcon(e)}
                  data-testid="drawer-search-icon"
                />
              </div>
            </div>
            {showActivesCart() && (
              <div className={styles['account-name-container-results']}>
                <div className={styles['account-name-container-results']}>
                  <div className={styles['account-name-container-results-title']}>
                    {t('cart.drawerForm.recentCarts')}
                  </div>
                  <div className={styles['account-name-container-results-wrapper']}>
                    {subCartsByUser.map(({ subCartId, amwayUser, proxySubCart }, i) => (
                      <CardRadioButton
                        onClick={onCardRadioClick}
                        disabled={
                          (isProxyOrder(cart.orderType) && proxySubCart) ||
                          amwayUser?.status === AmwayUserDetailsStatusEnum.Inactive
                        }
                        key={subCartId}
                        checked={checkedIndex === i}
                        mlpVersion={MLPVersion.ONE}
                        label={`${amwayUser?.firstName ?? ''} ${amwayUser?.lastName ?? ''}`}
                        name="account-number-or-name"
                        onChange={() =>
                          onRadioChange(
                            {
                              amwayUserId: amwayUser?.amwayUserId as string,
                              accountId: amwayUser?.accountId,
                              firstName: amwayUser?.firstName,
                              lastName: amwayUser?.lastName,
                            },
                            i,
                          )
                        }
                        cardDescription={`${t('cart.cartEllipsesMenu.accountNum')} ${amwayUser?.accountId}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            {showLoader ? <DownlineSearchLoader /> : renderAccounts()}
          </div>
        </Drawer>
      )}
    </>
  );
}
